import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { AppConfigService } from '../../services/app-config.service';

@Component({
  selector: 'app-set-password',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './set-password.component.html',
  styleUrls: ['./set-password.component.scss']
})
export class SetPasswordComponent implements OnInit {
  token: string = '';
  email: string = '';
  password: string = '';
  confirmPassword: string = '';
  userType: string = 'Client';

  isLoading = true;
  isSubmitting = false;
  isTokenValid = false;
  isPasswordSet = false;

  errorMessage: string = '';
  successMessage: string = '';

  private get apiUrl() { return this.config.apiUrl; }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private config: AppConfigService
  ) {}

  ngOnInit(): void {
    this.token = this.route.snapshot.queryParams['token'] || '';

    if (!this.token) {
      this.isLoading = false;
      this.errorMessage = 'No token provided. Please use the link from your email.';
      return;
    }

    this.validateToken();
  }

  validateToken(): void {
    this.http.post<any>(`${this.apiUrl}/api/clientusers/validate-setup-token`, { token: this.token })
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          this.isTokenValid = response.valid;
          this.email = response.email;
          this.userType = response.userType ?? 'Client';
        },
        error: (error) => {
          this.isLoading = false;
          this.isTokenValid = false;
          this.errorMessage = error.error?.error || 'Invalid or expired link. Please request a new password setup email.';
        }
      });
  }

  get passwordsMatch(): boolean {
    return this.password === this.confirmPassword;
  }

  get passwordStrength(): string {
    if (this.password.length < 8) return 'weak';
    if (this.password.length >= 12 && this.hasUppercase && this.hasNumber) return 'strong';
    return 'medium';
  }

  get hasMinLength(): boolean {
    return this.password.length >= 8;
  }

  get hasUppercase(): boolean {
    return /[A-Z]/.test(this.password);
  }

  get hasNumber(): boolean {
    return /[0-9]/.test(this.password);
  }

  setPassword(): void {
    if (!this.password || !this.confirmPassword) {
      this.errorMessage = 'Please fill in both password fields';
      return;
    }

    if (!this.passwordsMatch) {
      this.errorMessage = 'Passwords do not match';
      return;
    }

    if (this.password.length < 8) {
      this.errorMessage = 'Password must be at least 8 characters long';
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';

    const endpoint = this.userType === 'Admin'
      ? `${this.apiUrl}/api/adminauth/set-password`
      : `${this.apiUrl}/api/clientusers/set-password`;

    this.http.post<any>(endpoint, {
      token: this.token,
      password: this.password
    }).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        this.isPasswordSet = true;
        this.successMessage = response.message || 'Password set successfully!';
      },
      error: (error) => {
        this.isSubmitting = false;
        this.errorMessage = error.error?.error || 'Failed to set password. Please try again.';
      }
    });
  }

  goToLogin(): void {
    this.router.navigate(this.userType === 'Admin' ? ['/admin/login'] : ['/login']);
  }
}
